import { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { PreflightValidationResponse } from '@/pages/RewardCardList/components/NewRewardCardButton/types';
import { RewardCardsParams } from '@/pages/RewardCardList/types';
import { DatePaginatedTransactionsParams } from '@/pages/TransactionList/components/DatePaginatedTransactionsList/types';
import { PendingTransactionsParams } from '@/pages/TransactionList/components/PendingTransactionsList/types';
import PaginatedResponse, { DatePaginatedResponse } from '@/types/paginatedResponse';
import { QueryParams } from '@/types/query';
import RepositoryResponse from '@/types/repositoryResponse';
import { OperationsLog, RewardCardAccount, Transaction } from '@/types/rewardCard';
import ajax from '@/utils/ajax';
import logAjaxError from '@/utils/logAjaxError';
import { errorMessage } from '@/utils/mapAxiosErrorMessages';
import { toQueryString } from '@/utils/queryUtils';

export const rewardCardRepository = {
  async getAccountsForPerson({
    host,
    personId,
    params
  }: {
    host: string;
    personId: string;
    params: RewardCardsParams;
  }): Promise<PaginatedResponse<RewardCardAccount[]>> {
    const path = `/api/dugout/reward_cards/accounts/?person_id=${personId}`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params
    });

    return response.data;
  },

  async getAccount({
    host,
    accountId
  }: {
    host: string;
    accountId: string;
  }): Promise<RepositoryResponse<RewardCardAccount>> {
    try {
      const path = `/api/dugout/reward_cards/accounts/${accountId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get reward card account: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async createAccountPreflightValidation({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<PreflightValidationResponse> {
    const path = '/api/dugout/reward_cards/accounts/preflight_validation/';
    const response = await ajax.post<{ person_id: string }, AxiosResponse>({
      url: `${host}${path}`,
      data: { person_id: personId }
    });

    return response.data;
  },

  async createAccount({
    host,
    personId,
    params
  }: {
    host: string;
    personId: string;
    params?: { forward_balance: boolean };
  }): Promise<RewardCardAccount> {
    const path = '/api/dugout/reward_cards/accounts/';
    const response = await ajax.post<{ person_id: string }, AxiosResponse>({
      url: `${host}${path}`,
      data: { ...params, person_id: personId }
    });

    return response.data;
  },

  async terminateAccount({
    host,
    accountId
  }: {
    host: string;
    accountId: string;
  }): Promise<RewardCardAccount> {
    const path = `/api/dugout/reward_cards/accounts/${accountId}/terminate/`;
    const response = await ajax.put<object, AxiosResponse>({
      url: `${host}${path}`,
      data: {}
    });

    return response.data;
  },

  async setStatus({
    host,
    accountId,
    status
  }: {
    host: string;
    accountId: string;
    status: string;
  }): Promise<RewardCardAccount> {
    const path = `/api/dugout/reward_cards/accounts/${accountId}/set_card_status/`;
    const response = await ajax.put<{ card_status: string }, AxiosResponse>({
      url: `${host}${path}`,
      data: { card_status: status }
    });

    return response.data;
  },

  async getPendingTransactionsForPerson({
    host,
    personId,
    searchParams
  }: {
    host: string;
    personId: string;
    searchParams: PendingTransactionsParams;
  }): Promise<PaginatedResponse<Transaction[]>> {
    const path = `/api/dugout/reward_cards/transactions/?person_id=${personId}&is_pending=true`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params: searchParams
    });

    return response.data;
  },

  async getDatePaginatedTransactionsForPerson({
    host,
    personId,
    searchParams
  }: {
    host: string;
    personId: string;
    searchParams: DatePaginatedTransactionsParams;
  }): Promise<DatePaginatedResponse<Transaction[]>> {
    const path = `/api/dugout/reward_cards/transactions/date_paginated/?person_id=${personId}&is_pending=false`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params: searchParams
    });

    return response.data;
  },

  async getOperationsLogs({
    host,
    personId,
    params
  }: {
    host: string;
    personId: string;
    params?: QueryParams;
  }): Promise<PaginatedResponse<OperationsLog[]>> {
    const queryString = toQueryString({ ...params, person_id: personId });
    const path = `/api/dugout/reward_cards/operations_log/${queryString}`;
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async retryFailedOperation({
    host,
    operationsLogId
  }: {
    host: string;
    operationsLogId: string;
  }): Promise<void> {
    const path = `/api/dugout/reward_cards/operations_log/${operationsLogId}/retry_failed_operation/`;
    try {
      await ajax.post<object, AxiosResponse>({
        url: `${host}${path}`,
        data: {}
      });
    } catch (error) {
      await Promise.reject(`Operation sync retry failed. ${errorMessage(error as AxiosError)}`);
    }
  }
};

const RewardCardRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getAccountsForPerson(
      personId: string,
      params: RewardCardsParams
    ): Promise<PaginatedResponse<RewardCardAccount[]>> {
      return rewardCardRepository.getAccountsForPerson({ host, personId, params });
    },

    async getAccount(accountId: string): Promise<RepositoryResponse<RewardCardAccount>> {
      return rewardCardRepository.getAccount({ host, accountId });
    },

    async createAccountPreflightValidation(personId: string): Promise<PreflightValidationResponse> {
      return rewardCardRepository.createAccountPreflightValidation({ host, personId });
    },

    async createAccount(
      personId: string,
      params: { forward_balance: boolean }
    ): Promise<RewardCardAccount> {
      return rewardCardRepository.createAccount({ host, personId, params });
    },

    async terminateAccount(accountId: string): Promise<RewardCardAccount> {
      return rewardCardRepository.terminateAccount({ host, accountId });
    },

    async setStatus({
      accountId,
      status
    }: {
      accountId: string;
      status: string;
    }): Promise<RewardCardAccount> {
      return rewardCardRepository.setStatus({ host, accountId, status });
    },

    async getPendingTransactionsForPerson(
      personId: string,
      searchParams: PendingTransactionsParams
    ): Promise<PaginatedResponse<Transaction[]>> {
      return rewardCardRepository.getPendingTransactionsForPerson({ host, personId, searchParams });
    },

    async getDatePaginatedTransactionsForPerson(
      personId: string,
      searchParams: DatePaginatedTransactionsParams
    ): Promise<DatePaginatedResponse<Transaction[]>> {
      return rewardCardRepository.getDatePaginatedTransactionsForPerson({
        host,
        personId,
        searchParams
      });
    },

    async getOperationsLogs({
      personId,
      params
    }: {
      personId: string;
      params?: QueryParams;
    }): Promise<PaginatedResponse<OperationsLog[]>> {
      return rewardCardRepository.getOperationsLogs({ host, personId, params });
    },

    async retryFailedOperation({ operationsLogId }: { operationsLogId: string }): Promise<void> {
      return rewardCardRepository.retryFailedOperation({ host, operationsLogId });
    }
  };
};

export default RewardCardRepository;
